import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { dbClient } from '@/services/db-client';

type ReasonRow = { reason: string; label: string; users: number; pct: number };
type PersonRow = {
    display_name: string;
    email: string | null;
    reason: string;
    label: string;
    answered_at: string;
    returned: boolean;
};
type SurveyReport = {
    summary?: {
        days?: number;
        total?: number;
        sent?: number;
        returned?: number;
        by_reason?: ReasonRow[];
    };
    people?: PersonRow[];
};

function unwrap<T>(data: T | T[] | null | undefined): T | null {
    if (data == null) return null;
    return (Array.isArray(data) ? data[0] : data) ?? null;
}

function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

export function ChurnSurveyCard() {
    const query = useQuery({
        queryKey: ['admin', 'churn-survey'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SurveyReport>('admin_get_churn_survey', { p_days: 7 });
            if (error) throw new Error(error.message ?? 'No se pudo leer la encuesta');
            return unwrap(data);
        },
    });

    const summary = query.data?.summary;
    const people = query.data?.people ?? [];
    const reasons = summary?.by_reason ?? [];
    const total = Number(summary?.total ?? 0);
    const sent = Number(summary?.sent ?? 0);
    const returned = Number(summary?.returned ?? 0);

    return (
        <div className="rounded-xl border border-border bg-card px-4 py-4">
            <p className="text-sm font-semibold">Encuesta de abandono</p>
            <p className="mt-1 text-xs text-muted-foreground">
                Va con la regla de las 72 horas. El éxito de ese correo es que vuelvan a la app: lo ves en la columna
                Objetivo. Aquí está el motivo que eligieron en los últimos 7 días.
            </p>

            {query.isLoading && (
                <div className="flex justify-center py-6">
                    <Loader2 className="animate-spin text-muted-foreground" />
                </div>
            )}
            {query.isError && (
                <p className="mt-3 text-sm text-destructive">
                    {query.error instanceof Error ? query.error.message : 'No se pudo leer la encuesta'}
                </p>
            )}

            {!query.isLoading && !query.isError && (
                <>
                    <p className="mt-3 text-sm">
                        Enviados {sent}. Contestaron {total}. De quienes contestaron, volvieron a la app {returned}.
                    </p>
                    {reasons.length === 0 ? (
                        <p className="mt-2 text-sm text-muted-foreground">Esta semana nadie ha contestado.</p>
                    ) : (
                        <table className="mt-3 w-full text-sm">
                            <thead className="text-left text-muted-foreground">
                                <tr>
                                    <th className="py-2 font-medium">Motivo</th>
                                    <th className="py-2 text-right font-medium">Personas</th>
                                    <th className="py-2 text-right font-medium">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reasons.map((row) => (
                                    <tr key={row.reason} className="border-t border-border">
                                        <td className="py-2">{row.label}</td>
                                        <td className="py-2 text-right tabular-nums">{row.users}</td>
                                        <td className="py-2 text-right tabular-nums">{row.pct} %</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {people.length > 0 && (
                        <table className="mt-4 w-full text-sm">
                            <thead className="text-left text-muted-foreground">
                                <tr>
                                    <th className="py-2 font-medium">Nombre</th>
                                    <th className="py-2 font-medium">Correo</th>
                                    <th className="py-2 font-medium">Motivo</th>
                                    <th className="py-2 font-medium">Fecha</th>
                                    <th className="py-2 font-medium">Volvió</th>
                                </tr>
                            </thead>
                            <tbody>
                                {people.map((person) => (
                                    <tr key={`${person.email}-${person.answered_at}`} className="border-t border-border">
                                        <td className="py-2">{person.display_name}</td>
                                        <td className="py-2">{person.email || '—'}</td>
                                        <td className="py-2">{person.label}</td>
                                        <td className="py-2">{fmtDate(person.answered_at)}</td>
                                        <td className="py-2">{person.returned ? 'Sí' : 'No'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
}
